import {
  useContext,
  useEffect,
  useState,
} from 'react';
import {useParams} from 'react-router-dom';
import {ReactDiagram} from 'gojs-react';

import {
  colorTag,
} from 'src/state/dependency2';
import {GraphData} from 'src/types';
import {WorkspaceContext, SelectedIdsContext} from 'src/context';
import {
  LoadingPlaceholder,
  NoDataPlaceholder,
  NodeHierarchyList,
} from 'src/components';

import {Sidebar as SidebarContainer} from './styled';
import {initDiagram, fetchDiagramData} from './utils';


export const DiagramView = () => {
  const {currentWorkspace} = useContext(WorkspaceContext);
  const {selectedIds} = useContext(SelectedIdsContext);
  const {id: nodeId} = useParams<{id: string | undefined}>() ?? {};
  const [data, setData] = useState<GraphData | undefined>(undefined);

  const definitionId = currentWorkspace?.materializedViewDefinition.id;

  useEffect(() => {
    (async () => {
      if (nodeId && definitionId) {
        const {nodes, relationships} = await fetchDiagramData(nodeId) || {};
        setData({
          relationships: relationships?.filter(({endId, startId}) => selectedIds[endId] && selectedIds[startId]) || [],
          nodes: nodes?.filter(({id}) => selectedIds[id]) ?? [],
        });
      }
    })();
  }, [definitionId, nodeId, selectedIds]);

  return !data ? (
        <LoadingPlaceholder />
      ) : data.nodes.length === 0 ? (
        <NoDataPlaceholder>
          No usage data found
        </NoDataPlaceholder>
      ) : (
        <ReactDiagram
          initDiagram={initDiagram}
          divClassName="InvestigateUsages__DiagramView__diagram"
          nodeDataArray={data.nodes.map(({
            id,
            primaryLabel,
            identity,
            name,
            properties,
          }) => ({
            metadata: {
              type: primaryLabel,
              identity,
            },
            isHighlighted: id === nodeId,
            text: properties['shortName'] || name || identity,
            name,
            color: colorTag(primaryLabel).color,
            key: id,
            id,
            category: '', // Default category
          }))}
          linkDataArray={data.relationships.map(({
            startId,
            endId,
            id,
            type,
          }) => ({
            from: startId,
            to: endId,
            isDeEmphasized: type.startsWith('CONTAINS_') || type === 'GROUPS',
            key: id,
            type,
            // groups: true,
            color: colorTag(type, 'edge').color,
          })) }
          skipsDiagramUpdate={false}
        />
      );
};

export const Sidebar = () => {
  const {currentWorkspace} = useContext(WorkspaceContext);
  const {selectedIds, setSelectedIds} = useContext(SelectedIdsContext);
  const definitionId = currentWorkspace?.materializedViewDefinition.id;
  const {id} = useParams<{id: string | undefined}>() ?? {};

  const [data, setData] = useState<GraphData | undefined>(undefined);
  const [disclosedNodes, setExpandedNodes] = useState<{[key: string]: boolean}>({});

  useEffect(() => {
    (async () => {
      if (id && definitionId) {
        const newData = await fetchDiagramData(id);
        setData(newData);
        const allToggledOn = newData?.nodes?.reduce((accum, {id}) => ({
          ...accum,
          [id]: true,
        }), {}) ?? {};

        setSelectedIds(allToggledOn);
        setExpandedNodes(allToggledOn);
      }
    })();
  }, [definitionId, id]);

  return !data ? (
    <div style={{flex: '1 1 20%'}}>
      <LoadingPlaceholder />
    </div>
  ) : data.nodes.length === 0 ? (
    <div style={{flex: '1 1 20%'}}>
      <NoDataPlaceholder>
        No usage data found
      </NoDataPlaceholder>
    </div>
  ) : (
    <SidebarContainer>
      <NodeHierarchyList
        selectedNodes={selectedIds}
        disclosedNodes={disclosedNodes}
        onToggleDetail={(changedExpansions) => setExpandedNodes({
          ...disclosedNodes,
          ...changedExpansions,
        })}
        onSelectItem={(changedSelections) => setSelectedIds({
          ...selectedIds,
          ...changedSelections,
        })}
        rootNodeId={id}
        data={data}
      />
    </SidebarContainer>
  );
};
